/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import type moment from 'moment';

import { SHORT_MONTH_DAY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { Theme } from '@coursera/cds-core';
import { Accordion, AccordionHeader, AccordionPanel, Button, Grid } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import { CalendarEmptyState } from 'bundles/degree-home/components/v2/Calendar/CalendarEmptyState';
import { getCalendarItemsWithOverdue } from 'bundles/degree-home/components/v2/Calendar/CalendarUtils';
import { calendarListFooterId } from 'bundles/degree-home/components/v2/Calendar/constants/constants';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/degree-home';

import { AssignmentItem, LiveEventItem } from './CalendarListItem';
import { getNearestDate, getOrderedCalendarListItems } from './CalendarUtils';
import { filterOptionValues } from './constants/constants';

const styles: Record<string, Interpolation<Theme>> = {
  dateHeader: css`
    button:focus {
      outline: inherit;
    }
  `,
};

const getMobileTruncatedListItems = (dateInFocus: moment.Moment, calendarDates: string[]) => {
  let calendarOrderedListItems = getOrderedCalendarListItems(dateInFocus, calendarDates);

  // including overdue items, truncate the calendar item list removing past items
  const nearestDateWithOverdue = getNearestDate(dateInFocus, calendarOrderedListItems);
  calendarOrderedListItems =
    nearestDateWithOverdue === calendarListFooterId
      ? []
      : calendarOrderedListItems.slice(calendarOrderedListItems.indexOf(nearestDateWithOverdue));

  return calendarOrderedListItems;
};

export const MobileCalendarItemsList: React.FunctionComponent<{}> = () => {
  const { calendarItems, visibleCalendarItems, filter, dateInFocus, overdueItemsCount, setFilter } = useDegreeContext();
  const defaultDaysIncrement = 3;
  const [daysToDisplay, setDaysToDisplay] = useState(defaultDaysIncrement);

  const calendarListItems = getCalendarItemsWithOverdue(calendarItems, visibleCalendarItems, filter);
  const mobileCalendarTruncatedListItems = getMobileTruncatedListItems(dateInFocus, Object.keys(calendarListItems));

  // check if an overdue section is displayed
  const displayOverdueSection = useCallback(
    (startDay: number, endDay: number) => {
      return mobileCalendarTruncatedListItems.slice(startDay, endDay).includes(filterOptionValues.Overdue);
    },
    [mobileCalendarTruncatedListItems]
  );

  // when filter is at All, count the number of dismissed overdue items and show button in overdue section
  const overdueSectionDismissedItemCount =
    filter === filterOptionValues.All && calendarListItems[filterOptionValues.Overdue]
      ? overdueItemsCount - calendarListItems[filterOptionValues.Overdue].length
      : 0;
  const showViewMoreOverdueSectionButton = filter === filterOptionValues.All && overdueSectionDismissedItemCount !== 0;

  useEffect(() => {
    if (daysToDisplay <= 4 && displayOverdueSection(0, defaultDaysIncrement)) {
      // since overdue section isn't considered a day, display an additional day
      setDaysToDisplay(defaultDaysIncrement + 1);
    }
  }, [daysToDisplay, displayOverdueSection]);

  const onClickShowMore = () => {
    const moreDaysToDisplay = daysToDisplay + defaultDaysIncrement;
    // display an additional day when an overdue section is going to be included in the item list
    return displayOverdueSection(daysToDisplay, moreDaysToDisplay + 1)
      ? setDaysToDisplay(moreDaysToDisplay + 1)
      : setDaysToDisplay(moreDaysToDisplay);
  };

  return (
    <div id="calendarItemsContainerMobile" data-e2e="calendar-items-container-mobile">
      {mobileCalendarTruncatedListItems.slice(0, daysToDisplay).map((date) => (
        <Accordion key={`mobile-${date}`} id={`mobile-${date}`} variant="silent" defaultExpanded={true}>
          <AccordionHeader
            css={styles.dateHeader}
            label={
              date === filterOptionValues.Overdue
                ? _t('Overdue (#{overdueItemsCount})', { overdueItemsCount })
                : formatDateTimeDisplay(date, SHORT_MONTH_DAY_DISPLAY)
            }
          />
          <AccordionPanel>
            {calendarListItems[date].map((item: $TSFixMe) => {
              if (item.assignment) {
                return <AssignmentItem key={item.assignment.assignmentUrl} item={item.assignment} />;
              } else if (item.event) {
                return <LiveEventItem key={item.event.eventUrl} item={item.event} />;
              }
              return null;
            })}
            {showViewMoreOverdueSectionButton && date === filterOptionValues.Overdue && (
              <Button
                variant="ghost"
                size="small"
                icon={<ChevronNextIcon />}
                onClick={() => setFilter(filterOptionValues.Overdue)}
              >
                {_t('View #{overdueSectionDismissedItemCount} more', { overdueSectionDismissedItemCount })}
              </Button>
            )}
          </AccordionPanel>
        </Accordion>
      ))}
      {mobileCalendarTruncatedListItems.length > daysToDisplay ? (
        <Grid container alignItems="center" justifyContent="center">
          <Button
            data-e2e="show_more_calendar_items"
            variant="ghost"
            trackingName="show_more_days"
            component={TrackedButton}
            onClick={() => onClickShowMore()}
            withVisibilityTracking={false}
            requireFullyVisible={false}
          >
            {_t('Show More')}
          </Button>
        </Grid>
      ) : (
        <CalendarEmptyState mobile={true} />
      )}
    </div>
  );
};
